import { Button, Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useStores } from '../../../common/hooks';
import { QuestionnaireMediaType } from '../../../common/types/questionnaire-media-type';
import { FlexBox } from '../../shared-components/flex-box';
import './WinnerDetails.scss';
import { WinnerDetailsContactInformation } from './WinnerDetailsContactInformation';
import { WinnerDetailsEventAttendee } from './WinnerDetailsEventAttendee';
import { WinnerDetailsUploadedMedia } from './WinnerDetailsUploadedMedia';

interface WinnerDetailsProps {
  organizationName: string;
  questionnaireId?: number;
  onDownloadUploadedMedia(uploadedMediaId: number): void;
  onDeleteUploadedMedia(questionnaireId: number, uploadedMediaId: number): void;
  onUploadMedia(questionnaireId: number, mediaContent: File, mediaType: QuestionnaireMediaType): void;
  onRestoreMedia(questionnaireId: number, mediaType: QuestionnaireMediaType): void;
  onSaveQuestionnaire(questionnaireId: number): void;
}

const WinnerDetailsImpl: React.FC<WinnerDetailsProps> = (props) => {
  const {
    organizationName,
    questionnaireId,
    onDownloadUploadedMedia,
    onDeleteUploadedMedia,
    onRestoreMedia,
    onUploadMedia,
    onSaveQuestionnaire,
  } = props;

  const {
    winnersAndFinalistsPageUiStore
  } = useStores();

  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const questionnaire = winnersAndFinalistsPageUiStore.getWinnerQuestionnaire(questionnaireId);
  const questionnaireContactValidation = winnersAndFinalistsPageUiStore.getQuestionnaireContactValidation(questionnaire);
  const eventAttendeeValidations = winnersAndFinalistsPageUiStore.getEventAttendeeValidations(questionnaire);

  const isEditable = (): boolean => {
    return questionnaireId !== undefined && questionnaireId > 0;
  };

  const handleCancelEdit = (): void => {
    setIsEditMode(false);
    winnersAndFinalistsPageUiStore.resetEditedQuestionnaire(questionnaire.questionnaireId);
  }

  const handleSave = (): void => {
    setIsEditMode(false);
    onSaveQuestionnaire(questionnaire.questionnaireId);
  }

  const handleEdit = (): void => {
    if (!isEditable()) return;
    setIsEditMode(true);
  };

  if (!winnersAndFinalistsPageUiStore.selectedWinner) return null;

  return (
    <div className='winner-finalists-details'>
      <FlexBox
        isFullWidth
        content='space-between'
        aligns='center'
        className='winner-finalists-details__header'
      >
        <FlexBox
          aligns='center'
          className='header-title'
        >
          <Text
            size={500}
            weight='semibold'
            className='text--blue'
          >
            {winnersAndFinalistsPageUiStore.selectedWinner.nominationNumber}
          </Text>
          <Text
            size={500}
            weight='semibold'
          >
            {winnersAndFinalistsPageUiStore.selectedWinner.nominationName}
          </Text>
          <Text
            size={500}
            weight='semibold'
          >
            {organizationName}
          </Text>
        </FlexBox>
        <FlexBox className='header-buttons'>
          {
            isEditMode ?
              <>
                <Button
                  onClick={() => handleCancelEdit()}
                >
                  Cancel
                </Button>
                <Button
                  appearance='primary'
                  disabled={!questionnaireContactValidation.isValid}
                  onClick={() => handleSave()}
                >
                  Save
                </Button>
              </> :
              <Button
                disabled={!isEditable()}
                onClick={handleEdit}
              >
                Edit
              </Button>
          }
        </FlexBox>
      </FlexBox>
      <div className='winner-finalists-details__info'>
        <WinnerDetailsContactInformation
          isEditMode={isEditMode}
          winnerContact={questionnaire.contact}
          winnerContactValidation={questionnaireContactValidation}
        />
        {
          winnersAndFinalistsPageUiStore.selectedWinner.role === 'Winner' &&
          <>
            {
              winnersAndFinalistsPageUiStore.winnerEvents.length > 0 &&
              <FlexBox
                isFullWidth
                direction='column'
                className='winner-finalists-details__summary'
              >
                <Text
                  weight='semibold'
                  size={400}
                >
                  Event attendees
                </Text>
                {
                  questionnaire?.eventAttendees.map((eventAttendee, index) => {
                    return (
                      <WinnerDetailsEventAttendee
                        key={index}
                        eventAttendee={eventAttendee}
                        winnerEvent={winnersAndFinalistsPageUiStore.getWinnerEvent(eventAttendee.eventId)}
                        isEditMode={isEditMode}
                        validation={eventAttendeeValidations[index]}
                      />
                    )
                  })
                }
              </FlexBox>
            }
            <WinnerDetailsUploadedMedia
              isEditMode={isEditMode}
              questionnaire={questionnaire}
              logo={questionnaire.logo}
              media={questionnaire.media}
              onDownloadUploadedMedia={onDownloadUploadedMedia}
              onDeleteUploadedMedia={(uploadedMediaId) => onDeleteUploadedMedia(questionnaire.questionnaireId, uploadedMediaId)}
              onRestoreMedia={(mediaType) => onRestoreMedia(questionnaire.questionnaireId, mediaType)}
              onUploadMedia={(mediaContent, mediaType) => onUploadMedia(questionnaire.questionnaireId, mediaContent, mediaType)}
            />
          </>
        }
      </div>
    </div >
  );
}

export const WinnerDetails = observer(WinnerDetailsImpl);
